import React, { useCallback, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps, StyleClasses, ValueType, SelectableColumn } from '@paintscout/ui'
import type { ProductOption } from '@paintscout/util/builder'
import ClearIcon from '@material-ui/icons/Clear'
import type { OptionsDocument } from 'paintscout'
import InfoIcon from '@material-ui/icons/Info'
import { getProductOptions, updateProductOptions } from '@paintscout/util/builder'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Alert,
  InputField,
  TableRow,
  TableCell,
  ConfirmationDialog,
  useDialogs,
  ResultsTable
} from '@paintscout/ui'

const useStyles = makeStyles<Theme, EditBulkProductOptionDialogDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {},
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2)
    },
    cancel: {},
    confirm: {},
    alert: { display: 'flex', alignItems: 'center' },
    icon: {
      marginRight: theme.spacing(1),
      alignSelf: 'flex-start',
      color: theme.palette.info.main
    },
    table: {
      marginTop: theme.spacing(2)
    },
    tableColumn: {
      backgroundColor: '#2e3b51'
    },
    tableRow: {
      border: `1px solid ${theme.palette.divider}`,
      marginLeft: `-${theme.spacing(1)}px`
    }
  }),
  { name: 'EditBulkProductOptionDialogDialog' }
)

export interface EditBulkProductOptionDialogDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  keys: ValueType[]
  options: OptionsDocument

  onConfirm: (updatedOptions: OptionsDocument) => void
  onCancel?: (ev: React.MouseEvent<HTMLElement>) => void
}

function EditBulkProductOptionDialogDialog(props: EditBulkProductOptionDialogDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, options, ...baseDialogProps } = props
  const [isLoading, setIsLoading] = useState(false)
  const { openDialog, dismissDialog } = useDialogs()
  const items = getProductOptions({ options, quoteType: 'all', inactive: true }).filter((item) =>
    props.keys.includes(item.key as ValueType)
  )
  const [markupAmount, setMarkupAmount] = useState(0)
  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )

  const columns: SelectableColumn[] = [
    { name: 'Name', field: 'label', sortable: false },
    { name: 'Current Price', field: 'price', sortable: false },
    { name: 'After Price', field: 'after_price', sortable: false }
  ]

  const renderRow = useCallback(
    (row) => {
      return (
        <TableRow key={row._id} className={classes.tableRow}>
          <TableCell>{row.label}</TableCell>

          <TableCell>
            ${row.price}/{row?.unitLabel ?? 'gal'}
          </TableCell>

          <TableCell>
            ${getNewPrice({ oldPrice: row.price, markupPriceAmount: markupAmount })}/{row?.unitLabel ?? 'gal'}
          </TableCell>
        </TableRow>
      )
    },
    [items]
  )
  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2">Edit Products</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <Alert icon={null} severity={'info'} onClose={null}>
          <div className={classes.alert}>
            <div className={classes.icon}>
              <InfoIcon />
            </div>
            <Typography variant="body1">
              Enter a percentage below to adjust the prices of all selected products.
            </Typography>
          </div>
        </Alert>
        <InputField
          label={`Amount (%)`}
          format="custom"
          value={markupAmount}
          onChange={(ev) => {
            const value = ev.target.value
            setMarkupAmount(value ? parseFloat(value) : 0)
          }}
        />
        <ResultsTable
          className={classes.table}
          rowsPerPage={1000000}
          disablePagination
          columns={
            <TableRow>
              <>
                {columns.map((column) => {
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || 'left'}
                      fadeWhenInactive
                      className={classes.tableColumn}
                      sortable={false}
                      style={{ minWidth: column.minWidth || undefined }}
                    >
                      {column.name}
                    </TableCell>
                  )
                })}
              </>
            </TableRow>
          }
          renderRow={renderRow}
          rows={items}
          loading={isLoading}
        />
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.confirm}
          onClick={handleSubmit}
          variant={'contained'}
          disabled={isLoading || markupAmount === 0}
          data-testid="-dialog-confirm-button"
        >
          CONFIRM
        </Button>
      </DialogActions>
    </Dialog>
  )

  function getNewPrice({ oldPrice = 0, markupPriceAmount }) {
    return (oldPrice * (1 + (1 * markupPriceAmount) / 100))?.toFixed(2)
  }

  async function handleSubmit() {
    openDialog(ConfirmationDialog, {
      noLabel: 'BACK',
      yesLabel: 'CONFIRM',
      title: `Are you sure?`,
      message: (
        <Typography variant="body1">
          You are <strong>{markupAmount > 0 ? 'increasing' : 'decreasing'}</strong> the prices of the selected products
          by <strong>{Math.abs(markupAmount)}%</strong>.
        </Typography>
      ),
      onCancel: dismissDialog,
      onConfirm: () => {
        setIsLoading(true)
        let updatedOptions = { ...options }
        items.forEach((item) => {
          let newPrice = null
          newPrice = getNewPrice({
            oldPrice: item.price,
            markupPriceAmount: markupAmount
          })

          // Update options if item has update
          if (newPrice) {
            const newItem: ProductOption = {
              ...item,
              price: parseFloat(newPrice)
            }

            updatedOptions = updateProductOptions({ options: updatedOptions, productOptions: [newItem] })
          }
        })

        setIsLoading(false)
        onConfirm(updatedOptions)
      }
    })
  }
}

export default EditBulkProductOptionDialogDialog
